import { Button } from '@yeshuman/sdk'
import { Loader2, Upload } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'

import { jobCvAccept, useJobCvImports, useUploadJobCvs } from '../../hooks/use-job-cv-upload'
import { opportunityApplicantsQueryKey } from '../../hooks/use-opportunity-applicants'

export function JobCvUploadButton({ opportunityId }: { opportunityId: string }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const upload = useUploadJobCvs(opportunityId)
  const imports = useJobCvImports(opportunityId)
  const queryClient = useQueryClient()
  const completedIds = useRef(new Set<string>())
  const primed = useRef(false)
  const pendingCount = (imports.data ?? []).filter(
    (row) => row.status === 'pending' || row.parsing_status === 'processing',
  ).length

  useEffect(() => {
    if (!imports.data) return
    if (!primed.current) {
      for (const row of imports.data) completedIds.current.add(row.id)
      primed.current = true
      return
    }
    const newlyFinished = imports.data.filter(
      (row) => row.status !== 'pending' && !completedIds.current.has(row.id),
    )
    if (newlyFinished.length === 0) return
    for (const row of newlyFinished) completedIds.current.add(row.id)
    void queryClient.invalidateQueries({ queryKey: opportunityApplicantsQueryKey(opportunityId) })
  }, [imports.data, opportunityId, queryClient])

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={upload.isPending}
        onClick={() => inputRef.current?.click()}
      >
        {upload.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        ) : (
          <Upload className="h-4 w-4" aria-hidden />
        )}
        Upload CVs
        {pendingCount > 0 ? <span className="text-muted-foreground">({pendingCount} parsing)</span> : null}
      </Button>
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        accept={jobCvAccept()}
        multiple
        onChange={(event) => {
          const selected = Array.from(event.target.files ?? [])
          event.target.value = ''
          if (selected.length === 0) return
          upload.mutate(selected, {
            onSuccess: (result) => {
              toast.success(
                `Uploaded ${result.imports.length} CV${result.imports.length === 1 ? '' : 's'}. They join this job when parsing finishes.`,
              )
            },
            onError: (error) => {
              toast.error(error instanceof Error ? error.message : 'Failed to upload CVs')
            },
          })
        }}
      />
    </>
  )
}
